// import GA from 'utils/analytics';
import * as Alert from './alert';
import * as Accounts from './accounts';
import { request } from './api';

// Action types

export const SEND_MESSAGE = 'SEND_MESSAGE';
export const RECEIVE_MESSAGE = 'RECEIVE_MESSAGE';
export const SET_CONTEXT = 'SET_CONTEXT';
export const CLEAR = 'CLEAR';

export const STATEMENT_REQUEST = 'STATEMENT_REQUEST';
export const SEND_STATEMENT_SUCCESS = 'SEND_STATEMENT_SUCCESS';
export const SEND_STATEMENT_ERROR = 'SEND_STATEMENT_ERROR';
export const SEND_STATEMENT_ERROR_503 = 'SEND_STATEMENT_ERROR_503';
export const TASK_STATUS_REQUEST = 'TASK_STATUS_REQUEST';
export const TASK_STATUS_SUCCESS = 'TASK_STATUS_SUCCESS';
export const TASK_STATUS_ERROR = 'TASK_STATUS_ERROR';
export const TASK_STATUS_TIMEOUT = 'TASK_STATUS_TIMEOUT';

export const ANALYZE_SCORECARD = 'ANALYZE_SCORECARD';
export const ADD_SUMMARY_ITEM = 'ADD_SUMMARY_ITEM';
export const REMOVE_SUMMARY_ITEM = 'REMOVE_SUMMARY_ITEM';

// Contexts

/**
 * Action creators
 */
export const clear = () => ({ type: CLEAR });

export const sendMessage = (message) => ({ type: SEND_MESSAGE, message });

export const receiveMessage = (message) => ({ type: RECEIVE_MESSAGE, message });

export const setContext = (context) => ({ type: SET_CONTEXT, context });

// Statement Upload Feature
export function sendStatement(body) {
  return (dispatch) => {
    dispatch({ type: STATEMENT_REQUEST });
    // body is a FormData object, pass it as the body property in options
    return dispatch(request('post', `/aitools/holdings-upload/`, { body }))
      .then((data) => {
        const { task_id } = data;
        dispatch({
          type: SEND_STATEMENT_SUCCESS,
          taskID: task_id,
        });
        return task_id;
      })
      .catch((err) => {
        if (err && err.status === 429) {
          dispatch({
            type: SEND_STATEMENT_ERROR,
            fileSendError:
              'You have exceeded the limit of 25 uploads within the last 24 hours. Please try again later.',
          });
          dispatch(
            Alert.show({
              type: 'error',
              msg: 'You have exceeded the limit of 25 uploads within the last 24 hours. Please try again later.',
            })
          );
          return null;
        }

        if (err && err.status === 503) {
          dispatch({
            type: SEND_STATEMENT_ERROR_503,
            fileSendError:
              'The PDF upload service is currently unavailable from OpenAI. Please try again later or upload an image instead.',
          });

          return dispatch(
            Alert.show({
              type: 'error',
              msg: 'The server is temporarily unavailable (503). Please try again in a few minutes.',
            })
          );
        }

        dispatch({
          type: SEND_STATEMENT_ERROR,
          fileSendError: JSON.stringify(err),
        });
        dispatch(
          Alert.show({
            type: 'error',
            msg:
              JSON.stringify(err) ||
              'Error sending file. Please review your account information or select a new file.',
          })
        );
        return null;
      });
  };
}

export function pollTaskStatus(taskID, history) {
  return (dispatch) => {
    dispatch({ type: TASK_STATUS_REQUEST });

    const POLL_INTERVAL = 3000; // 3 second interval
    const MAX_POLL_ATTEMPTS = 60; // 3 minute limit

    let pollAttempts = 0;

    // Helper function to perform the GET request
    const checkTaskStatus = () => {
      dispatch(request('get', `/aitools/holdings-upload/${taskID}/`))
        // eslint-disable-next-line consistent-return
        .then((data) => {
          pollAttempts += 1;

          const { task_status, accounts } = data;

          if (task_status === 'SUCCESS') {
            // Image Upload returns response_status, PDF upload returns run_status.
            const response = data.run_status || data.response_status;

            if (response === 'completed') {
              if (accounts.length === 0) {
                dispatch({
                  type: TASK_STATUS_ERROR,
                  error:
                    'Job successful but returned empty. No account data extracted.',
                });
                clearInterval(pollingInterval);
              } else {
                dispatch({
                  type: TASK_STATUS_SUCCESS,
                  result: { accounts, response },
                });
                clearInterval(pollingInterval);
                dispatch(Accounts.fetchAll());
                return setTimeout(
                  history.push(`/accounts/custom/${accounts[0]}`),
                  1300
                );
              }
            }
            dispatch({
              type: TASK_STATUS_ERROR,
              error: 'Job failed, no account data extracted.',
            });
            clearInterval(pollingInterval);
          } else if (task_status === 'RUNNING') {
            if (pollAttempts >= MAX_POLL_ATTEMPTS) {
              dispatch({
                type: TASK_STATUS_TIMEOUT,
                error: 'Polling timed out. Max polling attempts reached.',
              });
              clearInterval(pollingInterval);
            }
          } else if (task_status === 'NOTFOUND') {
            dispatch({
              type: TASK_STATUS_ERROR,
              error: 'Task not found.',
            });
            clearInterval(pollingInterval);
          } else if (task_status.startsWith('TIMEOUT')) {
            dispatch({
              type: TASK_STATUS_TIMEOUT,
              error: 'Task timed out.',
            });
            clearInterval(pollingInterval);
          }
        })
        .catch((err) => {
          if (
            !(
              err.detail &&
              err.detail === 'Another request is still being processed.'
            )
          ) {
            dispatch({
              type: TASK_STATUS_ERROR,
              error: `Error fetching task status: ${JSON.stringify(err)}`,
            });
            clearInterval(pollingInterval);
          }
        });
    };

    const pollingInterval = setInterval(checkTaskStatus, POLL_INTERVAL);

    checkTaskStatus();
  };
}

// AI Scorecard Insights Feature

const analyzeScorecardPromptTest = `
Attached is a Wealthscope Portfolio Scorecard that analyzes an investment portfolio across several dimensions.

Task:
Using only the information shown in the Scorecard, generate a structured, client-facing summary that describes portfolio metrics and benchmark comparisons using neutral, factual language.
The output is informational only and must not assess suitability, desirability, or outcomes.
For sections with multiple available metrics (specifically Risk-Adjusted Performance and Downside Risk), select and present only a limited subset of representative metrics to improve clarity.
Metric selection must be based on representativeness of the measurement category (e.g., return efficiency, downside variability), not on performance, magnitude, or comparison outcomes. Do not imply that selected metrics are more important than omitted metrics.
Each section should begin with a single descriptive framing sentence that explains what type of portfolio characteristic the section describes (e.g., return variability, behavior during market declines), without evaluation or guidance.
Dimensions (include only if data is present):

1. Risk-Adjusted Performance
- Begin with one sentence describing that this section explains how portfolio returns relate to overall variability over the analysis period.
- State the analysis period using the format “month-year to month-year”.
- Identify and describe the benchmark, including composition and weighting.
- Select up to two representative metrics, prioritizing variety (for example, one return-based metric and one risk-adjusted ratio).
- Report the selected portfolio and benchmark values.
- Briefly describe what each selected metric measures.
- Do not list additional risk-adjusted metrics.

2. Downside Risk
- Begin with one sentence describing that this section focuses on portfolio behavior during periods of market decline.
- Select up to two representative downside risk metrics, prioritizing variety (for example, one drawdown-related metric and one downside variability metric).
- Report the selected portfolio and benchmark values where available.
- Briefly describe what each selected metric measures in declining market conditions.
- Do not list additional downside risk metrics.

3. Fees
- Begin with one sentence describing that this section outlines the cost structure of the portfolio.
- State the portfolio’s weighted management expense ratio (MER).
- Always include: “In Wealthscope, as a portfolio’s weighted MER approaches 3%, the associated score decreases.”
- If a score is shown, state it and explain how it relates to the Wealthscope scale.
4. Income
- Begin with one sentence describing that this section outlines the level of income generated by the portfolio.
- State the portfolio’s income yield.
- Always include: “In Wealthscope, income yields closer to 5% correspond to higher scores.”
- If a score is shown, explain how it relates to this internal reference scale.
- Do not comment on income adequacy or investor needs.5. Diversification
- Begin with one sentence explaining that this section describes how portfolio exposure is distributed across different dimensions.
- Present diversification across no more than three dimensions, selected from: correlation, geographic exposure, and sector exposure.
- Present each selected dimension in a separate sentence.
- When describing geographic and sector exposure, report holdings allocation percentages only.
- Do not include risk contribution percentages or allocation-versus-risk comparisons in this section.
- If relevant, include a neutral reference that: additional details on how these exposures contribute to portfolio risk are available in the Diversification tabs (Asset Class, Individual, Region, Global Stock Sector tabs only).
- Refer to individual securities only as “certain holdings” or “individual positions”.
- Do not name specific securities, funds, asset classes, or instruments.

Constraints (strict):
- Do not use the words: advise, recommend, should, important, better, worse, suitable, appropriate, improve, enhance, reduce, increase.
- No adjectives, qualifiers, or superlatives.
- No evaluation of results.
- No discussion of investor goals, preferences, or actions.- No opening or concluding sentences.
- Third-person only. Do not address the reader.
- Bold each section header using two asterisks (**).- Include the letter grade and score in parentheses beside each section header (e.g., **Risk-Adjusted Performance (A, 82%)**).
- Maximum length: 400 words.

Do not reference or enumerate omitted metrics.

The output must remain strictly descriptive and informational.`;

const analyzeScorecardPrompt = `
Attached is a Wealthscope Portfolio Scorecard that analyzes an investment portfolio in several dimensions.Using information from the scorecard in the PDF file alone, suggest high-level strategic enhancements to improve the portfolio in the following dimensions. Include the score for each dimension:
1. Risk-adjusted performance
2. Downside risk
3. Fees
4. Income (always start with the qualifier, “if income generation is important to you”)
5. Diversification (focus on correlations, sector and geographical diversification. Comment on individual asset risk contribution only if an asset has unusually high risk contribution compared to its asset allocation and/or return contribution. Do not comment on portfolio-specific risk.)
6. ESG (if there is no ESG information, do not mention this category)
Add a summary of actionable improvements at the end. Do not exceed 400 words in total. In your response, do not suggest or mention any specific financial products. No need for an opening or concluding sentence. Do not ask what the investor would like to do next at the end of the response or if they want to explore things further. Bold each header with two asterisks.`;
// export function listFiles(clientID) {
//   return (dispatch) => {
//     dispatch({ type: ANALYZE_SCORECARD });
//     // Upload the file to openAI
//     return api
//       .request('get', `/${clientID}/aitools/openai/files`)
//       .then((data) => {
//         console.log(data);
//       });
//   };
// }

// export function deleteFile(clientID, fileID) {
//   return (dispatch) => {
//     dispatch({ type: ANALYZE_SCORECARD });
//     // Upload the file to openAI
//     return api
//       .request('delete', `/${clientID}/aitools/openai/files/${fileID}`)
//       .then((data) => {
//         console.log(data);
//       });
//   };
// }

// api
//   .request('delete', `/${clientID}/aitools/openai/files/${fileID}`)
//   .then((dataDelete) => {
//     console.log(dataDelete);
//   });

//           api
// .request('get', `/${clientID}/aitools/openai/files`)
// .then((dataList) => {
//   console.log(dataList);
// });

export function analyzeScorecard(body, key) {
  return (dispatch, getState) => {
    const state = getState();
    const isFrench = state.Storage && state.Storage.language === 'fr';
    dispatch({ type: ANALYZE_SCORECARD });

    return dispatch(request('post', `/aitools/openai/files`, { body }))
      .then((file) => {
        const fileID = file.id;
        let prompt = analyzeScorecardPromptTest;
        if (isFrench) {
          prompt += ' Answer in Quebecois french.';
        }

        return dispatch(
          request('post', `/aitools/openai/responses`, {
            body: {
              model: 'gpt-4.1-mini',
              background: true,
              temperature: 0,
              input: [
                {
                  role: 'user',
                  content: [
                    { type: 'input_text', text: prompt },
                    {
                      type: 'input_file',
                      file_id: fileID,
                    },
                  ],
                },
              ],
            },
          })
        )
          .then((response) => {
            const responseID = response.id;

            const pollForResult = (retries = 90, delay = 2000) =>
              new Promise((resolve, reject) => {
                const attempt = () => {
                  dispatch(
                    request('get', `/aitools/openai/responses/${responseID}`)
                  )
                    .then((res) => {
                      if (res.status === 'completed') {
                        resolve(res);
                      } else if (res.status === 'failed') {
                        reject(
                          new Error(
                            'An error occurred while analyzing the scorecard. Please refresh your browser and try again.'
                          )
                        );
                      } else if (retries > 0) {
                        setTimeout(() => attempt((retries -= 1)), delay);
                      } else {
                        reject(
                          new Error(
                            'An error occurred while analyzing the scorecard. Please refresh your browser and try again.'
                          )
                        );
                      }
                    })
                    .catch((err) => reject(err));
                };

                attempt();
              });

            return pollForResult()
              .then((result) => {
                dispatch({
                  type: ADD_SUMMARY_ITEM,
                  id: key,
                  value: result.output[0].content[0].text,
                });

                // Clean up uploaded file
                return dispatch(
                  request('delete', `/aitools/openai/files/${fileID}`)
                );
              })
              .catch((pollError) => {
                dispatch({
                  type: TASK_STATUS_ERROR,
                  error:
                    pollError.message ||
                    'An error occurred while analyzing the scorecard. Please refresh your browser and try again.',
                });

                dispatch(
                  Alert.show({
                    type: 'error',
                    msg:
                      pollError.message ||
                      'An error occurred while analyzing the scorecard. Please refresh your browser and try again.',
                  })
                );

                // Try to delete the file anyway
                return dispatch(
                  request('delete', `/aitools/openai/files/${fileID}`)
                );
              });
          })
          .catch((responseError) => {
            dispatch({
              type: TASK_STATUS_ERROR,
              error:
                responseError.message ||
                'An error occurred while analyzing the scorecard. Please refresh your browser and try again.',
            });

            dispatch(
              Alert.show({
                type: 'error',
                msg:
                  responseError.message ||
                  'An error occurred while analyzing the scorecard. Please refresh your browser and try again.',
              })
            );

            // Attempt to clean up file
            return dispatch(
              request('delete', `/aitools/openai/files/${fileID}`)
            );
          });
      })
      .catch((uploadError) => {
        dispatch({
          type: TASK_STATUS_ERROR,
          error:
            uploadError.message ||
            'An error occurred while analyzing the scorecard. Please refresh your browser and try again.',
        });

        dispatch(
          Alert.show({
            type: 'error',
            msg:
              uploadError.message ||
              'An error occurred while analyzing the scorecard. Please refresh your browser and try again.',
          })
        );
      });
  };
}

export function removeAnalyzeScorecard(key) {
  return (dispatch) => {
    dispatch({
      type: REMOVE_SUMMARY_ITEM,
      id: key,
    });
  };
}

// export function testAddSummary(id, value) {
//   return (dispatch) => {
//     dispatch({
//       type: ADD_SUMMARY_ITEM,
//       id,
//       value,
//     });
//   };
// }
